import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { useNavigate, useNavigationType, useParams } from "react-router-dom";
import { GridColumnHeaderParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { Button, Box as MuiBox } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStore } from "../../store";
import Title from "../../components/Title";
import { DataGrid } from "../../components";
import { useDidMountEffect } from "../../utils/custom";
import { countryLabel } from "./Modules/Handler";
import KeywordSearch from "./Modules/KeywordSearch";
import {
  KEYWORD_SORT_OPTIONS,
  KeywordSortBy,
  displayKeyword,
  matchTypeLabel,
  parseKeywordSearch,
} from "./keywordParams";

import { Wrap, SummaryBar } from "./styles";

const sortLabel = (value: KeywordSortBy) =>
  KEYWORD_SORT_OPTIONS.find((o) => o.value === value)?.label ?? value;

/** 그리드 컬럼 field → 서버 sortBy 키 */
const SORT_FIELDS: Record<string, KeywordSortBy> = {
  viewCount: "view",
  likeCounts: "like",
  downloadCounts: "download",
  shareCounts: "share",
  aiTotal: "aiScore",
};

/**
 * Keywords 드릴다운 — 특정 해시태그가 붙은 템플릿 목록. `/keywords/:keyword`
 * 국가/기간은 인기 목록에서 확정한 필터(store, kw_ 쿠키)를 그대로 쓴다.
 */
const KeywordTemplates = () => {
  const { keyword = "" } = useParams<{ keyword: string }>();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const {
    KeywordsStore: {
      country,
      matchType,
      startDate,
      endDate,
      detail,
      sortBy,
      gridOption: { columns, rows, totalCount, loading, page, rowsPerPage, columnGroupingModel },
      searchKeyword,
      fetchKeywordTemplates,
      sortKeywordTemplatesBy,
      onPageChange,
      onPageSizeChange,
    },
  } = useStore();

  // 라우트 파라미터를 해석한다. react-router 가 이미 디코드하므로 "#diwali" 또는
  // "happy diwali" 형태다. # 이 있으면 해시태그, 없으면 본문 텍스트.
  const parsed = parseKeywordSearch(keyword);
  const routeKeyword = parsed?.keyword ?? "";
  const routeMatchType = parsed?.matchType ?? matchType;

  useEffect(() => {
    // 템플릿 디테일에서 뒤로 돌아온 경우(POP) 같은 키워드·유형의 rows 가 살아 있으면
    // 그대로 둔다 (page / 스크롤 유지).
    const same = detail.keyword === routeKeyword && matchType === routeMatchType;
    if (navigationType === "POP" && same && rows.length > 0) return;
    // URL 이 말하는 유형으로 스토어를 맞춘 뒤 조회한다 (searchKeyword 가 둘 다 처리).
    if (searchKeyword(keyword) === null) return;
    fetchKeywordTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  useDidMountEffect(() => {
    fetchKeywordTemplates();
  }, [page, rowsPerPage]);

  const headerClickEvent = (item: GridColumnHeaderParams) => {
    const key = SORT_FIELDS[item.field];
    if (!key) return;
    sortKeywordTemplatesBy(key);
  };

  return (
    <Wrap>
      <Title
        label={`Keywords · ${displayKeyword(detail.keyword || routeKeyword, matchType)}`}
        isDivider
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/keywords")}
          sx={{ textTransform: "none" }}
        >
          Back to popular keywords
        </Button>
      </Title>
      <MuiBox
        sx={{
          pt: "10px",
          display: "flex",
          alignItems: "flex-start",
          gap: "16px",
        }}
      >
        <SummaryBar sx={{ flex: 1 }}>
          <span>
            Country <strong>{countryLabel(country)}</strong>
          </span>
          <span>
            Keyword type <strong>{matchTypeLabel(matchType)}</strong>
          </span>
          <span>
            Published{" "}
            <strong>
              {dayjs(startDate).format("YYYY-MM-DD")} ~{" "}
              {dayjs(endDate).format("YYYY-MM-DD")}
            </strong>{" "}
            (UTC)
          </span>
          <span>
            Templates <strong>{detail.templateCount.toLocaleString()}</strong>
          </span>
          <span>
            Sorted by <strong>{sortLabel(sortBy)}</strong> (desc)
          </span>
        </SummaryBar>
        <KeywordSearch
          // 해시태그 모드면 # 을 붙여 채워, 그대로 다시 제출해도 같은 유형이 된다.
          initialValue={displayKeyword(detail.keyword || routeKeyword, matchType)}
        />
      </MuiBox>
      <DataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        page={page}
        rowHeight={110}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        handleColumnHeaderClick={headerClickEvent}
        columnGroupingModel={columnGroupingModel}
      />
    </Wrap>
  );
};

export default observer(KeywordTemplates);
